import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssociationWizard } from '../AssociationWizard';
import {
  BillingToggleSection,
  buildBillingLabels
} from '../shared/BillingToggleSection';
import { ConfirmAndRunStep, SummaryRow } from '../shared/ConfirmAndRunStep';
import { RuleViolation } from '../../helpers/types';
import {
  CredentialsStep,
  CredentialsFormValues,
  isCredentialsValid,
  trimCredentials,
  buildCredentialsLabels
} from '../shared/CredentialsStep';
import {
  SelectChildrenStep,
  SelectChildrenStepLabels,
  ChildWorkspace,
  isSelectChildrenValid,
  getSelectedChildNames,
  MAX_SELECTED_CHILDREN
} from './parts/SelectChildrenStep';

const WIZARD = 'tenantsAssociation.mspParentWizard';
const SELECT = `${WIZARD}.selectChildrenStep`;
const CREDENTIALS = `${WIZARD}.credentialsStep`;
const CONFIRM = `${WIZARD}.confirmStep`;

const ALL_PARENT_ACTIONS = [
  'ACTION_CLOUDINFRA_AUTH',
  'ACTION_CREATE_CHILD',
  'ACTION_ASSIGN_PAYG',
  'ACTION_ASSOCIATE_SASE',
  'ACTION_DISCONNECT_CHARGEBEE',
  'ACTION_UPDATE_SUBSCRIPTION'
] as const;

export interface MspParentAssociationPayload {
  credentials: CredentialsFormValues;
  selectedChildIds: readonly string[];
}

interface MspParentWizardProps {
  /** Upfront validation failures from the last rejected attempt. */
  upfrontErrors?: RuleViolation[];
  childWorkspaces: readonly ChildWorkspace[];
  isLoadingChildren?: boolean;
  selectedChildIds: readonly string[];
  onSelectedChildIdsChange: (next: string[]) => void;
  credentials: CredentialsFormValues;
  onCredentialsChange: (next: CredentialsFormValues) => void;
  scheduledConfirmed: boolean;
  onScheduledConfirmedChange: (next: boolean) => void;
  onBack: () => void;
  onFinish: (payload: MspParentAssociationPayload) => void;
  isFinishing?: boolean;
  onStepChange?: (stepIndex: number) => void;
}

const MspParentWizard = ({
  upfrontErrors,
  childWorkspaces,
  isLoadingChildren,
  selectedChildIds,
  onSelectedChildIdsChange,
  credentials,
  onCredentialsChange,
  scheduledConfirmed,
  onScheduledConfirmedChange,
  onBack,
  onFinish,
  isFinishing,
  onStepChange
}: MspParentWizardProps) => {
  const { t } = useTranslation('activity');

  const handleFinish = useCallback(
    () =>
      onFinish({
        credentials: trimCredentials(credentials),
        selectedChildIds
      }),
    [onFinish, credentials, selectedChildIds]
  );

  // The batch cap is stated in every string that mentions it rather than
  // hardcoded in the copy, so raising MAX_SELECTED_CHILDREN cannot leave the
  // wizard telling the operator the old number.
  const selectChildrenLabels = useMemo<SelectChildrenStepLabels>(
    () => ({
      sectionTitle: t(`${SELECT}.SECTION_TITLE`),
      infoBody: t(`${SELECT}.INFO_BODY`, { max: MAX_SELECTED_CHILDREN }),
      listTitle: t(`${SELECT}.LIST_TITLE`),
      selectAll: t(`${SELECT}.SELECT_ALL`),
      selectFirst: t(`${SELECT}.SELECT_FIRST`, { max: MAX_SELECTED_CHILDREN }),
      unselectAll: t(`${SELECT}.UNSELECT_ALL`),
      selectedCount: t(`${SELECT}.SELECTED_COUNT`, {
        selected: selectedChildIds.length,
        max: MAX_SELECTED_CHILDREN
      }),
      limitReached: t(`${SELECT}.LIMIT_REACHED`, {
        max: MAX_SELECTED_CHILDREN
      }),
      cancelledTag: t(`${SELECT}.CANCELLED_TAG`),
      cancelledWarningTitle: t(`${SELECT}.CANCELLED_WARNING_TITLE`),
      cancelledWarningBody: t(`${SELECT}.CANCELLED_WARNING_BODY`),
      emptyState: t(`${SELECT}.EMPTY_STATE`)
    }),
    [t, selectedChildIds.length]
  );

  const selectedNames = useMemo(
    () => getSelectedChildNames(childWorkspaces, selectedChildIds),
    [childWorkspaces, selectedChildIds]
  );

  const summaryRows = useMemo<SummaryRow[]>(
    () => [
      {
        label: t(`${CONFIRM}.SUMMARY_PARENT_UUID`),
        value: credentials.parentUuid
      },
      {
        label: t(`${CONFIRM}.SUMMARY_ADMIN_EMAIL`),
        value: credentials.adminEmail
      },
      {
        label: t(`${CONFIRM}.SUMMARY_CHILDREN`),
        value: selectedNames.join(', ')
      },
      {
        label: t(`${CONFIRM}.SUMMARY_BILLING`),
        value: t(`${CONFIRM}.BILLING_MOVE_PAYG`)
      }
    ],
    [t, credentials.parentUuid, credentials.adminEmail, selectedNames]
  );

  const actions = useMemo(
    () => ALL_PARENT_ACTIONS.map(key => t(`${CONFIRM}.${key}`)),
    [t]
  );

  const steps = useMemo(
    () => [
      {
        content: (
          <SelectChildrenStep
            childWorkspaces={childWorkspaces}
            isLoading={isLoadingChildren}
            selectedIds={selectedChildIds}
            onChange={onSelectedChildIdsChange}
            labels={selectChildrenLabels}
          />
        ),
        isValid: !isLoadingChildren && isSelectChildrenValid(selectedChildIds),
        subtitle: t(`${SELECT}.PAGE_SUBTITLE`)
      },
      {
        content: (
          <CredentialsStep
            values={credentials}
            onChange={onCredentialsChange}
            labels={buildCredentialsLabels(t, WIZARD, {
              withSectionTitle: true
            })}
          >
            <BillingToggleSection labels={buildBillingLabels(t, WIZARD)} />
          </CredentialsStep>
        ),
        isValid: isCredentialsValid(credentials),
        subtitle: t(`${CREDENTIALS}.PAGE_SUBTITLE`)
      },
      {
        content: (
          <ConfirmAndRunStep
            scheduledConfirmed={scheduledConfirmed}
            onConfirmChange={onScheduledConfirmedChange}
            summaryRows={summaryRows}
            actions={actions}
            actionsTitle={t(`${CONFIRM}.ACTIONS_TITLE`)}
            confirmCheckboxLabel={t(`${CONFIRM}.CONFIRMATION_CHECKBOX`)}
            upfrontErrors={upfrontErrors}
            upfrontErrorsTitle={t(`${CONFIRM}.VALIDATION_FAILED_TITLE`)}
          />
        ),
        isValid: scheduledConfirmed,
        subtitle: t(`${CONFIRM}.PAGE_SUBTITLE`)
      }
    ],
    [
      childWorkspaces,
      isLoadingChildren,
      selectedChildIds,
      onSelectedChildIdsChange,
      selectChildrenLabels,
      credentials,
      onCredentialsChange,
      scheduledConfirmed,
      onScheduledConfirmedChange,
      summaryRows,
      actions,
      upfrontErrors,
      t
    ]
  );

  return (
    <AssociationWizard
      steps={steps}
      nextLabel={t(`${WIZARD}.NEXT`)}
      previousLabel={t(`${WIZARD}.PREVIOUS`)}
      finishLabel={t(`${WIZARD}.RUN_MIGRATION`)}
      onBack={onBack}
      onFinish={handleFinish}
      isFinishing={isFinishing}
      onStepChange={onStepChange}
    />
  );
};

export default MspParentWizard;
